import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GetBookingByManageTokenAction,
  GetManageAvailableSlotsAction,
  UpdateBookingByManageTokenAction,
  CancelBookingByManageTokenAction,
} from "../../store/action";
import {
  labelClass,
  fieldClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";
import { isWallClockPast } from "../../utils/dates";
import { notify, notifySaveError } from "../../utils/notify";

/**
 * The page an emailed manage link opens.
 *
 * Deliberately account-free. Public booking is anonymous — it creates a
 * Customer row, never a User — so the customer most likely to need this is
 * exactly the one who cannot sign in. Before this page existed their only
 * options were to create an account in order to cancel a haircut, or to phone
 * the salon; most did neither and simply did not turn up.
 *
 * The token in the URL is the whole authorisation. There is nothing to log in
 * to and nothing to remember.
 */

/** Local date key (YYYY-MM-DD) — never `toISOString`, which shifts to UTC. */
const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

/**
 * The booking's own moment, formatted for reading.
 *
 * `BookingDateTime` arrives as the business's wall-clock time carrying a `Z`,
 * so it is parsed as UTC and read back as UTC. Using the browser's local zone
 * would re-shift a value that was never really UTC, and show a customer in
 * another country an hour they were never given.
 */
const formatWhen = (iso, locale) => {
  const date = new Date(iso);

  const day = date.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });

  return { day, time };
};

const cardClass =
  "rounded-card border border-ink-200 bg-white p-6 shadow-card sm:p-8";

const ManageBooking = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  // What could not be *loaded* (the booking, the free times). The outcome of a
  // reschedule or a cancellation is the snackbar's (utils/notify.js).
  const [error, setError] = useState(null);

  const [mode, setMode] = useState("view"); // view | reschedule | confirmCancel
  const [busy, setBusy] = useState(false);

  const [chosenDate, setChosenDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [chosenSlot, setChosenSlot] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBooking(await GetBookingByManageTokenAction(token));
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? { key: "ManageBooking.linkInvalid" }
          : { key: "ManageBooking.loadError" },
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchSlots = async (date) => {
    setChosenDate(date);
    setChosenSlot("");
    setSlots([]);
    if (!date) return;

    setSlotsLoading(true);
    setError(null);
    try {
      setSlots(await GetManageAvailableSlotsAction(token, date));
    } catch (err) {
      setError({ key: "ManageBooking.slotsError" });
    } finally {
      setSlotsLoading(false);
    }
  };

  const submitReschedule = async () => {
    setBusy(true);
    try {
      const updated = await UpdateBookingByManageTokenAction(token, {
        bookingDate: chosenDate,
        timeSlot: chosenSlot,
      });
      setBooking(updated);
      setMode("view");
      notify.success(t("ManageBooking.movedNotice"));
    } catch (err) {
      // 409: someone took that time while the page was open. Nothing is
      // broken and the booking is unchanged — pick another time.
      if (err?.response?.status === 409) {
        notify.warning(
          err.response.data?.message || t("ManageBooking.slotTaken"),
        );
      } else {
        notifySaveError(err, t("ManageBooking.saveError"));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    setBusy(true);
    try {
      setBooking(await CancelBookingByManageTokenAction(token));
      setMode("view");
      notify.success(t("ManageBooking.cancelledNotice"));
    } catch (err) {
      notifySaveError(err, t("ManageBooking.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const message = (item) =>
    item?.message ?? (item?.key ? t(item.key) : null);

  if (loading) {
    return (
      <Shell>
        <div className={`${cardClass} text-center`}>
          <p className="m-0 text-fluid-sm text-ink-500">
            {t("ManageBooking.loading")}
          </p>
        </div>
      </Shell>
    );
  }

  // A dead link is the one case with nothing to show, so it gets the page to
  // itself rather than an error strip above an empty card.
  if (!booking) {
    return (
      <Shell>
        <div className={`${cardClass} text-center`}>
          <i
            className="bi bi-link-45deg mb-3 block text-3xl text-ink-400"
            aria-hidden="true"
          />
          <h1 className="m-0 mb-2 text-xl font-bold text-ink-900">
            {t("ManageBooking.linkInvalidTitle")}
          </h1>
          <p className="m-0 text-fluid-sm text-ink-500">
            {message(error) ?? t("ManageBooking.linkInvalid")}
          </p>
        </div>
      </Shell>
    );
  }

  const { day, time } = formatWhen(booking.bookingDateTime, i18n.language);
  const isCancelled = booking.status === "Cancelled";
  // The display above reads the wall clock (timeZone: "UTC"), so this comparison
  // has to as well — `new Date(...) <= new Date()` weighs a wall-clock time
  // against a real instant and calls an appointment past a whole offset early
  // or late. The server is the authority; this only decides whether to offer
  // the buttons.
  const isPast = isWallClockPast(booking.bookingDateTime);
  const canChange = !isCancelled && !isPast;

  return (
    <Shell>
      <div className={cardClass}>
        <p className="m-0 mb-1 text-xs font-semibold tracking-wide text-brand-700 uppercase">
          {booking.organizationName}
        </p>
        <h1 className="m-0 mb-5 text-2xl font-bold text-ink-900">
          {isCancelled
            ? t("ManageBooking.titleCancelled")
            : t("ManageBooking.title")}
        </h1>

        {error && (
          <div
            className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-fluid-sm text-red-700"
            role="alert"
          >
            {message(error)}
          </div>
        )}

        <dl className="m-0 grid gap-x-6 gap-y-3 sm:grid-cols-[auto_1fr]">
          <Detail label={t("ManageBooking.when")}>
            <span className={isCancelled ? "line-through opacity-60" : ""}>
              {day} · {time}
            </span>
          </Detail>
          <Detail label={t("ManageBooking.service")}>
            {booking.serviceName}
          </Detail>
          <Detail label={t("ManageBooking.with")}>{booking.resourceName}</Detail>
          {booking.organizationAddress && (
            <Detail label={t("ManageBooking.where")}>
              {booking.organizationAddress}
            </Detail>
          )}
          <Detail label={t("ManageBooking.code")}>
            <span className="font-mono">{booking.confirmationCode}</span>
          </Detail>
        </dl>

        {isCancelled && (
          <p className="mt-6 mb-0 text-fluid-sm text-ink-500">
            {t("ManageBooking.cancelledHelp")}
          </p>
        )}

        {isPast && !isCancelled && (
          <p className="mt-6 mb-0 text-fluid-sm text-ink-500">
            {t("ManageBooking.pastHelp")}
          </p>
        )}

        {canChange && mode === "view" && (
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              className={primaryButtonClass}
              onClick={() => setMode("reschedule")}
            >
              {t("ManageBooking.reschedule")}
            </button>
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-full border border-ink-200 bg-white px-6 py-3 text-sm font-semibold text-ink-700 transition hover:border-red-400 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
              onClick={() => setMode("confirmCancel")}
            >
              {t("ManageBooking.cancel")}
            </button>
          </div>
        )}

        {canChange && mode === "confirmCancel" && (
          <div className="mt-7 rounded-lg border border-red-200 bg-red-50 p-5">
            <p className="m-0 mb-4 text-fluid-sm font-semibold text-red-800">
              {t("ManageBooking.cancelConfirm")}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                disabled={busy}
                onClick={submitCancel}
                className="inline-flex w-full items-center justify-center rounded-full bg-red-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-red-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy
                  ? t("ManageBooking.working")
                  : t("ManageBooking.cancelConfirmAction")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setMode("view")}
                className="inline-flex w-full items-center justify-center rounded-full border border-ink-200 bg-white px-6 py-3 text-sm font-semibold text-ink-700 transition hover:border-ink-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {t("ManageBooking.keep")}
              </button>
            </div>
          </div>
        )}

        {canChange && mode === "reschedule" && (
          <div className="mt-7 border-t border-ink-100 pt-6">
            <h2 className="m-0 mb-4 text-base font-semibold text-ink-900">
              {t("ManageBooking.pickNewTime")}
            </h2>

            <label className={labelClass} htmlFor="manage-date">
              {t("ManageBooking.date")}
            </label>
            <input
              id="manage-date"
              type="date"
              className={fieldClass}
              value={chosenDate}
              min={toDateKey(new Date())}
              onChange={(event) => fetchSlots(event.target.value)}
            />

            {slotsLoading && (
              <p className="mt-4 mb-0 text-fluid-sm text-ink-500">
                {t("ManageBooking.slotsLoading")}
              </p>
            )}

            {!slotsLoading && chosenDate && slots.length === 0 && (
              <p className="mt-4 mb-0 text-fluid-sm text-ink-500">
                {t("ManageBooking.noSlots")}
              </p>
            )}

            {slots.length > 0 && (
              <div className="mt-5">
                <p className={labelClass}>{t("ManageBooking.time")}</p>
                <div className="flex flex-wrap gap-2">
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      aria-pressed={chosenSlot === slot}
                      onClick={() => setChosenSlot(slot)}
                      className={`min-h-11 rounded-lg border px-4 py-2 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                        chosenSlot === slot
                          ? "border-brand-600 bg-brand-600 text-white"
                          : "border-ink-200 bg-white text-ink-700 hover:border-brand-400"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                disabled={busy || !chosenSlot}
                onClick={submitReschedule}
                className={primaryButtonClass}
              >
                {busy
                  ? t("ManageBooking.working")
                  : t("ManageBooking.confirmMove")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setMode("view");
                  setError(null);
                }}
                className="inline-flex w-full items-center justify-center rounded-full border border-ink-200 bg-white px-6 py-3 text-sm font-semibold text-ink-700 transition hover:border-ink-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {t("ManageBooking.back")}
              </button>
            </div>
          </div>
        )}

        <p className="mt-7 mb-0 border-t border-ink-100 pt-5 text-xs text-ink-400">
          {t("ManageBooking.contactHelp")}
          {booking.organizationPhone && (
            <>
              {" "}
              <span className="font-semibold text-ink-600">
                {booking.organizationPhone}
              </span>
            </>
          )}
        </p>
      </div>

      {booking.organizationSlug && (
        <p className="mt-5 text-center text-fluid-sm">
          <Link to={`/${booking.organizationSlug}`} className={textLinkClass}>
            {t("ManageBooking.bookAgain", { name: booking.organizationName })}
          </Link>
        </p>
      )}
    </Shell>
  );
};

const Detail = ({ label, children }) => (
  <>
    <dt className="text-xs font-semibold tracking-wide text-ink-500 uppercase sm:pt-0.5">
      {label}
    </dt>
    <dd className="m-0 mb-2 text-fluid-base text-ink-900 sm:mb-0">{children}</dd>
  </>
);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-ink-50 px-4 py-10 sm:py-16">
    <div className="mx-auto w-full max-w-xl">{children}</div>
  </div>
);

export default ManageBooking;
